import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AgentArchitecture,
  ChatMessage,
  ChatMessageRole,
  ChatUserInfo,
  ContentType,
  ContextInfo,
  InvocationContextAuthState,
  InvocationContextChannel,
  InvocationContextMedium,
  KeyValuePair,
  SessionEvent,
  SessionEventType,
  SessionMessageData,
  SessionToolCallData,
  StreamingGenerateChatAnswerRequest,
  StreamingGenerateChatAnswerResponse,
  StreamingGenerateChatAnswerResponseContentChunk,
  StreamingGenerateChatAnswerResponseFunctionCompleteChunk,
  StreamingGenerateChatAnswerResponseFunctionProgressChunk,
  StreamingGenerateChatAnswerResponseFunctionStartChunk,
  StreamingGenerateChatAnswerResponseProposedActionChunk,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { streamingGenerateChatAnswerRequestToJson } from '@vendasta/protos/ai_assistants/v1alpha1/codec';
import { resolveRpcHost } from '@vendasta/protos';
import { EnvironmentService, SessionService } from '@galaxy/core';
import { catchError, EMPTY, map, Observable, Subject, take, tap } from 'rxjs';
import {
  approveProposedAction$ as approveProposedActionRpc$,
  rejectProposedAction$ as rejectProposedActionRpc$,
} from '@vendasta/protos/ai_assistants/v1alpha1/session-service';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

import { ACCOUNT_GROUP_ID_TOKEN, PARTNER_ID_TOKEN } from '../../core/tokens';
import { getNamespace, TypeAndIdFromNamespace } from '../../core/services/ai-assistant-utils';
import {
  AskQuestionsArgs,
  AssistantTextMessage,
  ChatUIMessage,
  PlanAction,
  PlanMessage,
  ProposedActionMessage,
  QuestionsMessage,
  SubmitPlanArgs,
  ToolCallEvent,
  ToolCallState,
  WorkingMessage,
} from './generation-chat.types';
import { ProposedActionApproval, ProposedActionRejection } from '../ai-proposed-action/ai-proposed-action.types';
import { AiProposedActionComponent } from '../ai-proposed-action/ai-proposed-action.component';
import { SessionHistoryService } from '../generation-chat-history/session-history.service';
import jwt_decode from 'jwt-decode';
import { InvocationContextApp } from '@vendasta/protos/ai_assistants/v1alpha1';

const SSE_ENDPOINT = '/api/streaming-chat';
const DEFAULT_ASSISTANT_ID = 'ASSISTANT-chat-receptionist';
const PLAN_APPROVED_MESSAGE = 'Plan approved, continue';
const PLAN_REJECTED_MESSAGE = 'plan rejected';
const OUTPUT_DISPLAY_TOOLS = new Set(['SubmitPlan', 'AskQuestions']);

interface ErrorResponse {
  errorMessage?: string;
  error?: string;
}

interface SSEEvent {
  type: string;
  data: Partial<StreamingGenerateChatAnswerResponse> | ErrorResponse;
}

@Injectable()
export class GenerationChatService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly sessionService = inject(SessionService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly partnerId = toSignal(inject(PARTNER_ID_TOKEN));
  private readonly environmentService = inject(EnvironmentService);
  private readonly sessionHistoryService = inject(SessionHistoryService);

  private readonly _messages = signal<ChatUIMessage[]>([]);
  private readonly _isStreaming = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _sessionId = signal<string>(crypto.randomUUID());
  private readonly _extraTags = signal<string[]>([]);
  private readonly _generationType = signal<string>('');
  private readonly authSessionId = this.sessionService.getSessionIdSignal();
  readonly userId = computed<string | null>(() => {
    const sid = this.authSessionId();
    return sid ? this.extractUserId(sid) : null;
  });

  readonly messages = this._messages.asReadonly();
  readonly isStreaming = this._isStreaming.asReadonly();
  readonly error = this._error.asReadonly();
  readonly extraTags = this._extraTags.asReadonly();
  readonly namespace = computed(() => getNamespace(this.accountGroupId(), this.partnerId()));
  readonly sessionTags = computed<string[]>(() => {
    const tags: string[] = [AgentArchitecture.GENERATION];
    const gt = this._generationType();
    if (gt) tags.push(gt);
    tags.push(...this._extraTags());
    const uid = this.userId();
    if (uid) tags.push(uid);
    return tags;
  });

  private readonly _toolCall$ = new Subject<ToolCallEvent>();
  readonly toolCall$ = this._toolCall$.asObservable();

  private readonly _pendingToolCalls = new Map<string, ToolCallEvent>();
  private readonly _functionComplete$ = new Subject<ToolCallEvent>();
  readonly functionComplete$ = this._functionComplete$.asObservable();

  private readonly _streamReset$ = new Subject<void>();
  readonly streamReset$ = this._streamReset$.asObservable();

  private assistantId = DEFAULT_ASSISTANT_ID;
  private extraContextVariables: KeyValuePair[] = [];

  private activeToolCalls = new Map<string, { messageIndex: number; toolCallIndex: number }>();
  private abortController: AbortController | null = null;
  private currentAssistantText = '';
  private currentSubmitPlan: SubmitPlanArgs | null = null;
  private currentAskQuestions: AskQuestionsArgs | null = null;
  private noOpOutputRendered = false;
  private lastWorkingContent: string | null = null;
  private currentWorkingMessageIndex = -1;
  _streamPromise: Promise<void> | null = null;

  get sessionId(): string {
    return this._sessionId();
  }

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.abortController?.abort();
      this._toolCall$.complete();
      this._streamReset$.complete();
      this._functionComplete$.complete();
    });
  }

  setAssistantId(id: string): void {
    this.assistantId = id;
  }

  setGenerationType(type: string): void {
    this._generationType.set(type);
  }

  setExtraContextVariables(variables: KeyValuePair[]): void {
    this.extraContextVariables = variables;
  }

  setExtraTags(tags: string[]): void {
    this._extraTags.set(tags);
  }

  startNewSession(): void {
    this.abortInFlightStream();
    this.resetTransientState();
    this._messages.set([]);
    this._error.set(null);
    this._isStreaming.set(false);
    this._sessionId.set(crypto.randomUUID());
  }

  loadSession(sessionId: string): Observable<void> {
    const namespace = this.namespace();
    if (!namespace) {
      return new Observable<void>((subscriber) => {
        this._error.set('Namespace not available');
        subscriber.next();
        subscriber.complete();
      });
    }

    this.abortInFlightStream();
    return this.sessionHistoryService.getSessionEvents$(namespace, sessionId, 100).pipe(
      tap((events) => {
        this.resetTransientState();
        this._error.set(null);
        this._isStreaming.set(false);
        this._sessionId.set(sessionId);
        this.rebuildFromEvents(events);
      }),
      catchError((err) => {
        this._error.set(err instanceof Error ? err.message : 'Failed to load session');
        return EMPTY;
      }),
      map(() => undefined),
    );
  }

  private abortInFlightStream(): void {
    if (!this.abortController) return;
    this.abortController.abort();
    this.abortController = null;
    this._streamReset$.next();
  }

  private resetTransientState(): void {
    this.currentAssistantText = '';
    this.currentSubmitPlan = null;
    this.currentAskQuestions = null;
    this.noOpOutputRendered = false;
    this.lastWorkingContent = null;
    this.currentWorkingMessageIndex = -1;
    this.activeToolCalls.clear();
    this._pendingToolCalls.clear();
  }

  private rebuildFromEvents(events: SessionEvent[]): void {
    const ordered = [...events].sort((a, b) => {
      const at = a.created?.getTime() ?? 0;
      const bt = b.created?.getTime() ?? 0;
      return at - bt;
    });

    const suppressedAssistantTexts = this.collectOutputDisplayTexts(ordered);

    const uiMessages: ChatUIMessage[] = [];
    let workingGroup: WorkingMessage | null = null;
    let lastPlan: PlanMessage | null = null;

    const flushWorking = (): void => {
      if (workingGroup) {
        uiMessages.push(workingGroup);
        workingGroup = null;
      }
    };

    for (const event of ordered) {
      const timestamp = event.created ?? new Date();
      const eventId = event.eventId || crypto.randomUUID();

      switch (event.eventType) {
        case SessionEventType.SESSION_EVENT_TYPE_MESSAGE: {
          const message = event.message;
          if (!message) break;
          const content = this.messageContent(message);
          if (!content.trim()) break;
          flushWorking();
          if (message.role === ChatMessageRole.CHAT_MESSAGE_ROLE_USER) {
            const action = this.planActionFromUserMessage(content);
            if (action && lastPlan && lastPlan.action === undefined) lastPlan.action = action;
            uiMessages.push({ id: eventId, type: 'user', text: content, timestamp });
          } else if (message.role === ChatMessageRole.CHAT_MESSAGE_ROLE_ASSISTANT) {
            if (suppressedAssistantTexts.has(content.trim())) break;
            uiMessages.push({ id: eventId, type: 'assistant-text', text: content, timestamp });
          }
          break;
        }
        case SessionEventType.SESSION_EVENT_TYPE_TOOL_CALL: {
          const toolCall = event.toolCall;
          if (!toolCall) break;
          const parsedArgs = this.tryParseArgs(toolCall.arguments);

          if (toolCall.functionName === 'SubmitPlan') {
            flushWorking();
            const planMessage = this.planMessageFromArgs(parsedArgs, eventId, timestamp);
            if (planMessage) {
              if (planMessage.type === 'plan') lastPlan = planMessage;
              uiMessages.push(planMessage);
            }
          } else if (toolCall.functionName === 'AskQuestions') {
            flushWorking();
            const questionsMessage = this.questionsMessageFromArgs(parsedArgs, eventId, timestamp);
            if (questionsMessage) uiMessages.push(questionsMessage);
          } else {
            if (!workingGroup) workingGroup = { id: eventId, type: 'working', toolCalls: [], timestamp };
            workingGroup.toolCalls.push({
              functionName: toolCall.functionName,
              functionCallId: toolCall.functionCallId || eventId,
              state: this.toolCallState(toolCall),
            });
          }
          break;
        }
        case SessionEventType.SESSION_EVENT_TYPE_PROPOSED_ACTION: {
          flushWorking();
          uiMessages.push(this.proposedActionMessage(eventId, timestamp));
          break;
        }
        default:
          break;
      }
    }

    flushWorking();
    this._messages.set(uiMessages);
  }

  private collectOutputDisplayTexts(events: SessionEvent[]): Set<string> {
    const texts = new Set<string>();
    for (const event of events) {
      if (event.eventType !== SessionEventType.SESSION_EVENT_TYPE_TOOL_CALL) continue;
      const toolCall = event.toolCall;
      if (!toolCall || !OUTPUT_DISPLAY_TOOLS.has(toolCall.functionName)) continue;
      const parsedArgs = this.tryParseArgs(toolCall.arguments);
      const output = typeof parsedArgs?.['Output'] === 'string' ? parsedArgs['Output'].trim() : '';
      if (output) texts.add(output);
    }
    return texts;
  }

  private messageContent(message: SessionMessageData): string {
    if (message.content) return message.content;
    return message.contentParts?.length ? message.contentParts[0].text || '' : '';
  }

  private planActionFromUserMessage(content: string): PlanAction | null {
    if (content === PLAN_APPROVED_MESSAGE) return 'approved';
    if (content === PLAN_REJECTED_MESSAGE) return 'rejected';
    return null;
  }

  private planMessageFromArgs(
    parsedArgs: Record<string, unknown> | null,
    id: string,
    timestamp: Date,
  ): PlanMessage | AssistantTextMessage | null {
    if (!parsedArgs) return null;
    const parsed = parsedArgs as unknown as SubmitPlanArgs;
    if (parsed.plan) return { id, type: 'plan', plan: parsed, timestamp };
    if (parsed.Output) return { id, type: 'assistant-text', text: parsed.Output, timestamp };
    return null;
  }

  private questionsMessageFromArgs(
    parsedArgs: Record<string, unknown> | null,
    id: string,
    timestamp: Date,
  ): QuestionsMessage | AssistantTextMessage | null {
    if (!parsedArgs) return null;
    const parsed = parsedArgs as unknown as AskQuestionsArgs;
    if (parsed.questions?.length) {
      return {
        id,
        type: 'questions',
        questions: parsed.questions.map((q, index) => ({
          id: `${index}`,
          text: q.text,
          type: q.type,
          options: q.options,
        })),
        output: parsed.Output ?? '',
        timestamp,
      };
    }
    if (parsed.Output) return { id, type: 'assistant-text', text: parsed.Output, timestamp };
    return null;
  }

  private toolCallState(toolCall: SessionToolCallData): ToolCallState {
    return toolCall.status === 'ERROR' || toolCall.errorMessage ? 'error' : 'complete';
  }

  private proposedActionMessage(sessionEventId: string, timestamp: Date): ProposedActionMessage {
    const { namespaceType, namespaceId } = TypeAndIdFromNamespace(this.namespace());
    return {
      id: sessionEventId,
      type: 'proposed-action',
      proposedAction: { sessionEventId, namespaceType, namespaceId },
      timestamp,
    };
  }

  sendMessage(text: string, contextInfo?: Partial<ContextInfo>): void {
    const id = crypto.randomUUID();
    const userMessage: Partial<ChatMessage> = {
      role: ChatMessageRole.CHAT_MESSAGE_ROLE_USER,
      content: text,
      externalId: id,
    };

    this._messages.update((msgs) => [...msgs, { id, type: 'user', text, timestamp: new Date() }]);

    this.streamRequest(userMessage, contextInfo);
  }

  approvePlan(plan: string): void {
    this.markLastPlanAction('approved');
    const contextInfo: Partial<ContextInfo> = {
      variables: [{ key: 'ApprovedPlan', value: plan }],
    };
    this.sendMessage(PLAN_APPROVED_MESSAGE, contextInfo);
  }

  rejectPlan(): void {
    this.markLastPlanAction('rejected');
    this.sendMessage(PLAN_REJECTED_MESSAGE);
  }

  answerQuestions(compiledAnswers: string): void {
    this.sendMessage(compiledAnswers);
  }

  private markLastPlanAction(action: PlanAction): void {
    this._messages.update((msgs) => {
      const updated = [...msgs];
      for (let i = updated.length - 1; i >= 0; i--) {
        const msg = updated[i];
        if (msg.type === 'plan' && msg.action === undefined) {
          updated[i] = { ...msg, action };
          break;
        }
      }
      return updated;
    });
  }

  private streamRequest(userMessage: Partial<ChatMessage>, contextInfo?: Partial<ContextInfo>): void {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this._streamReset$.next();
    this._isStreaming.set(true);
    this._error.set(null);
    this.resetTransientState();

    const userAuthSessionId = this.authSessionId();
    if (!userAuthSessionId) {
      this._error.set(`Request failed: no session found`);
      this._isStreaming.set(false);
      return;
    }

    const namespace = this.namespace();
    const finalContextInfo = this.buildContextInfo(contextInfo);

    const request: Partial<StreamingGenerateChatAnswerRequest> = {
      assistantKey: {
        id: this.assistantId,
        namespace: namespace,
      },
      chatHistory: [userMessage],
      chatUserInfo: {} as ChatUserInfo,
      contextInfo: finalContextInfo as ContextInfo,
      agentArchitecture: AgentArchitecture.GENERATION,
      sessionId: this.sessionId,
      tags: this.sessionTags(),
      invocationContext: {
        medium: InvocationContextMedium.TEXT,
        authState: InvocationContextAuthState.AUTHENTICATED,
        channel: InvocationContextChannel.PLATFORM,
        app: InvocationContextApp.UNSET_APP,
      },
    };

    this._streamPromise = this.startSSEStream(request, userAuthSessionId, this.abortController.signal);
  }

  private buildContextInfo(extra?: Partial<ContextInfo>): Partial<ContextInfo> {
    const variables: KeyValuePair[] = [];
    if (extra?.variables) variables.push(...extra.variables);
    const gt = this._generationType();
    if (gt !== '') {
      variables.push({ key: 'GenerationType', value: gt });
    }
    if (this.extraContextVariables.length > 0) variables.push(...this.extraContextVariables);
    return { variables };
  }

  private extractUserId(sessionId: string): string | null {
    try {
      const claims = jwt_decode<{ user_id?: string }>(sessionId);
      return claims?.user_id ?? null;
    } catch {
      return null;
    }
  }

  private async startSSEStream(
    request: Partial<StreamingGenerateChatAnswerRequest>,
    userAuthSessionId: string,
    signal: AbortSignal,
  ): Promise<void> {
    try {
      const response = await fetch(this.getApiUrl(SSE_ENDPOINT), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userAuthSessionId}`,
          Accept: 'text/event-stream',
        },
        body: JSON.stringify(streamingGenerateChatAnswerRequestToJson(request)),
        signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        this._error.set(`Request failed: ${response.status} ${errorText}`);
        this._isStreaming.set(false);
        return;
      }

      await this.readSSEStream(response);
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        if (signal === this.abortController?.signal) {
          this._isStreaming.set(false);
        }
        return;
      }
      this._error.set(error instanceof Error ? error.message : 'Stream failed');
      this._isStreaming.set(false);
    }
  }

  private async readSSEStream(response: Response): Promise<void> {
    if (!response.body) {
      this._error.set('Response body is null');
      this._isStreaming.set(false);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      let done = false;
      while (!done) {
        const result = await reader.read();
        done = result.done;
        if (done) continue;

        buffer += decoder.decode(result.value, { stream: true });

        const parsed = this.parseSSEBuffer(buffer);
        buffer = parsed.remaining;

        for (const event of parsed.events) {
          this.handleSSEEvent(event);
        }
      }
    } finally {
      reader.releaseLock();
    }

    if (this._isStreaming()) {
      this.finalizeStream();
    }
  }

  private parseSSEBuffer(buffer: string): { events: SSEEvent[]; remaining: string } {
    const events: SSEEvent[] = [];
    const blocks = buffer.split(/\r?\n\r?\n/);
    const remaining = blocks.pop() || '';

    for (const block of blocks) {
      if (!block.trim()) continue;

      let eventType = '';
      let eventData = '';

      for (const line of block.split(/\r?\n/)) {
        if (line.startsWith('event:')) {
          eventType = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          eventData = line.slice(5).trim();
        }
      }

      if (eventType && eventData) {
        try {
          events.push({ type: eventType, data: JSON.parse(eventData) });
        } catch {
          console.warn('Failed to parse SSE data:', eventData);
        }
      }
    }

    return { events, remaining };
  }

  private handleSSEEvent(event: SSEEvent): void {
    if (event.type == 'error') {
      const errorData = event.data as ErrorResponse;
      this._error.set(errorData.errorMessage ?? errorData.error ?? 'Unknown error');
      this._isStreaming.set(false);
      return;
    }
    const eventData = event.data as Partial<StreamingGenerateChatAnswerResponse>;

    switch (event.type) {
      case 'content':
        this.handleContentEvent(eventData.content);
        break;
      case 'function_start':
        this.handleFunctionStartEvent(eventData.functionStart);
        break;
      case 'function_complete':
        this.handleFunctionCompleteEvent(eventData.functionComplete);
        break;
      case 'proposed_action':
        this.handleProposedAction(eventData.proposedAction);
        break;
      case 'final':
        this.finalizeStream();
        break;
      case 'function_progress':
        this.handleFunctionProgressEvent(eventData.functionProgress);
        break;
      case 'start':
      case 'metadata':
      case 'stream_end':
      case 'input_validation':
        break;
    }
  }

  private handleContentEvent(data: Partial<StreamingGenerateChatAnswerResponseContentChunk> | undefined): void {
    const content = data?.content ?? '';
    const contentType = data?.contentType;

    if (contentType === ContentType.WORKING) {
      if (content.trim()) this.lastWorkingContent = content;
      return;
    }

    if (contentType === ContentType.ANSWER && this.noOpOutputRendered) return;

    if (contentType === ContentType.REASONING || contentType === ContentType.ANSWER || !contentType) {
      this.currentAssistantText += content;
    }
  }

  private finalAnswerText(): string {
    // When a noOp tool already showed the answer, only reasoning text may remain; don't fall back to WORKING.
    const working = this.noOpOutputRendered ? null : this.lastWorkingContent;
    const text = working ?? this.currentAssistantText;
    // Drop a leading bracketed label the model sometimes prepends (e.g. "[Final Answer]").
    return text.replace(/^\s*\[[^\]]*\]\s*/, '');
  }

  private handleFunctionStartEvent(
    data: Partial<StreamingGenerateChatAnswerResponseFunctionStartChunk> | undefined,
  ): void {
    const functionName = data?.functionName ?? '';
    const functionCallId = data?.functionCallId ?? '';
    const argumentsJson = data?.arguments ?? '';
    const noOp = data?.noOp === true;
    const parsedArgs = this.tryParseArgs(argumentsJson);

    if (functionName === 'SubmitPlan') {
      this.handleSubmitPlan(parsedArgs);
    } else if (functionName === 'AskQuestions') {
      this.handleAskQuestions(parsedArgs);
    } else if (noOp) {
      this.handleNoOpToolCall(parsedArgs);
    } else {
      this.appendWorkingToolCall(functionName, functionCallId);
    }

    const event: ToolCallEvent = {
      functionName,
      functionCallId,
      arguments: argumentsJson,
      parsedArgs,
      noOp,
    };
    this._pendingToolCalls.set(functionCallId, event);
    this._toolCall$.next(event);
  }

  private tryParseArgs(argsJson: string): Record<string, unknown> | null {
    if (!argsJson) return null;
    try {
      const parsed = JSON.parse(argsJson);
      return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }

  private handleNoOpToolCall(parsedArgs: Record<string, unknown> | null): void {
    const output = typeof parsedArgs?.['Output'] === 'string' ? (parsedArgs['Output'] as string) : '';
    if (!output.trim()) return;

    this._messages.update((msgs) => [
      ...msgs,
      { id: crypto.randomUUID(), type: 'assistant-text', text: output, timestamp: new Date() },
    ]);
    this.noOpOutputRendered = true;
  }

  private appendWorkingToolCall(functionName: string, functionCallId: string): void {
    this._messages.update((msgs) => {
      const updated = [...msgs];
      const newToolCall = { functionName, functionCallId, state: 'running' as const };

      if (this.currentWorkingMessageIndex === -1 || updated[this.currentWorkingMessageIndex]?.type !== 'working') {
        const newIndex = updated.length;
        updated.push({ id: crypto.randomUUID(), type: 'working', toolCalls: [newToolCall], timestamp: new Date() });
        this.currentWorkingMessageIndex = newIndex;
        this.activeToolCalls.set(functionCallId, { messageIndex: newIndex, toolCallIndex: 0 });
      } else {
        const existing = updated[this.currentWorkingMessageIndex];
        if (existing.type === 'working') {
          const nextToolCalls = [...existing.toolCalls, newToolCall];
          updated[this.currentWorkingMessageIndex] = { ...existing, toolCalls: nextToolCalls };
          this.activeToolCalls.set(functionCallId, {
            messageIndex: this.currentWorkingMessageIndex,
            toolCallIndex: nextToolCalls.length - 1,
          });
        }
      }

      return updated;
    });
  }

  private handleFunctionCompleteEvent(
    data: Partial<StreamingGenerateChatAnswerResponseFunctionCompleteChunk> | undefined,
  ): void {
    const functionCallId = data?.functionCallId ?? '';

    const pending = this._pendingToolCalls.get(functionCallId);
    if (pending) {
      this._functionComplete$.next(pending);
      this._pendingToolCalls.delete(functionCallId);
    }

    this.resolveToolCall(functionCallId, 'complete');
  }

  private handleFunctionProgressEvent(
    data: Partial<StreamingGenerateChatAnswerResponseFunctionProgressChunk> | undefined,
  ): void {
    if (data?.status !== 'ERROR') return;
    const functionCallId = data.functionCallId ?? '';
    this._pendingToolCalls.delete(functionCallId);
    this.resolveToolCall(functionCallId, 'error');
  }

  private resolveToolCall(functionCallId: string, state: 'complete' | 'error'): void {
    const location = this.activeToolCalls.get(functionCallId);
    if (!location) return;

    this._messages.update((msgs) => {
      const updated = [...msgs];
      const existing = updated[location.messageIndex];
      if (existing?.type === 'working') {
        const nextToolCalls = existing.toolCalls.map((tc, i) => (i === location.toolCallIndex ? { ...tc, state } : tc));
        updated[location.messageIndex] = { ...existing, toolCalls: nextToolCalls };
      }
      return updated;
    });
    this.activeToolCalls.delete(functionCallId);
  }

  private finalizeStream(): void {
    this.resolveDanglingToolCalls();
    this.finalizeAssistantText();
    this._isStreaming.set(false);
    this.currentSubmitPlan = null;
    this.currentAskQuestions = null;
  }

  private resolveDanglingToolCalls(): void {
    if (this.activeToolCalls.size === 0) return;
    this._messages.update((msgs) =>
      msgs.map((m) =>
        m.type === 'working'
          ? {
            ...m,
            toolCalls: m.toolCalls.map((tc) => (tc.state === 'running' ? { ...tc, state: 'complete' as const } : tc)),
          }
          : m,
      ),
    );
    this.activeToolCalls.clear();
  }

  private finalizeAssistantText(): void {
    if (this.currentSubmitPlan || this.currentAskQuestions) return;
    const text = this.finalAnswerText();
    if (text.trim()) {
      this._messages.update((msgs) => [
        ...msgs,
        {
          id: crypto.randomUUID(),
          type: 'assistant-text',
          text,
          timestamp: new Date(),
        },
      ]);
    }
  }

  private handleSubmitPlan(parsedArgs: Record<string, unknown> | null): void {
    if (!parsedArgs) return;
    const parsed = parsedArgs as unknown as SubmitPlanArgs;
    this.currentSubmitPlan = parsed;

    if (parsed.plan) {
      this._messages.update((msgs) => [
        ...msgs,
        { id: crypto.randomUUID(), type: 'plan', plan: parsed, timestamp: new Date() },
      ]);
    } else if (parsed.Output) {
      this._messages.update((msgs) => [
        ...msgs,
        { id: crypto.randomUUID(), type: 'assistant-text', text: parsed.Output, timestamp: new Date() },
      ]);
    }
  }

  private handleAskQuestions(parsedArgs: Record<string, unknown> | null): void {
    if (!parsedArgs) return;
    const parsed = parsedArgs as unknown as AskQuestionsArgs;
    this.currentAskQuestions = parsed;

    if (parsed.questions?.length) {
      this._messages.update((msgs) => [
        ...msgs,
        {
          id: crypto.randomUUID(),
          type: 'questions',
          questions: parsed.questions.map((q, index) => {
            return { id: `${index}`, text: q.text, type: q.type, options: q.options };
          }),
          output: parsed.Output ?? '',
          timestamp: new Date(),
        },
      ]);
    } else if (parsed.Output) {
      this._messages.update((msgs) => [
        ...msgs,
        { id: crypto.randomUUID(), type: 'assistant-text', text: parsed.Output, timestamp: new Date() },
      ]);
    }
  }

  private handleProposedAction(
    data: Partial<StreamingGenerateChatAnswerResponseProposedActionChunk> | undefined,
  ): void {
    const sessionEventId = data?.sessionEventId ?? '';
    const namespaceId = data?.namespaceId ?? '';
    const namespaceType = data?.namespaceType ?? '';
    if (!sessionEventId) return;

    this._messages.update((msgs) => [
      ...msgs,
      {
        id: crypto.randomUUID(),
        type: 'proposed-action',
        proposedAction: { sessionEventId, namespaceId, namespaceType },
        timestamp: new Date(),
      },
    ]);
  }

  approveProposedAction(event: ProposedActionApproval, actionComponent: AiProposedActionComponent): void {
    const namespace = this.namespace();
    approveProposedActionRpc$({
      namespace,
      sessionId: event.sessionId,
      eventId: event.eventId,
    })
      .pipe(
        take(1),
        catchError(() => {
          this.snackbarService.openErrorSnack('Failed to approve proposed action');
          return EMPTY;
        }),
      )
      .subscribe(() => {
        actionComponent.reload();
        this.sendMessage(event.feedback);
      });
  }

  rejectProposedAction(event: ProposedActionRejection, actionComponent: AiProposedActionComponent): void {
    const namespace = this.namespace();
    rejectProposedActionRpc$({
      namespace,
      sessionId: event.sessionId,
      eventId: event.eventId,
      feedback: event.feedback,
    })
      .pipe(
        take(1),
        catchError(() => {
          this.snackbarService.openErrorSnack('Failed to reject proposed action');
          return EMPTY;
        }),
      )
      .subscribe(() => {
        actionComponent.reload();
        this.sendMessage(event.feedback);
      });
  }

  private getApiUrl(path: string): string {
    const host = resolveRpcHost(
      'ai_assistants.v1alpha1.AssistantService',
      this.environmentService.getEnvironmentString(),
    );
    return `${host}${path}`;
  }
}
